import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';
import fetchData from '../../functions/fetchData';
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import './WeeklySales.css';
import fetchDataFromTwoTables from '../../functions/fetchdatawithTwoTables';

function WeeklySales() {
  const [weeklySalesData, setWeeklySalesData] = useState([]);
  const [paymentMethodData, setPaymentMethodData] = useState([]);
  const [categoryWiseData, setCategoryWiseData] = useState([]);
  const [summaryData, setSummaryData] = useState({
    totalSales: 0,
    totalTransactions: 0,
    averagePerDay: 0,
    bestDay: '',
    bestDaySales: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(() => {
    const today = new Date();
    const weekStart = new Date(today.setDate(today.getDate() - today.getDay()));
    return weekStart.toISOString().split('T')[0];
  });

  const fetchWeeklySalesData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Calculate week end date
      const weekStart = new Date(selectedWeek);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      
      // Fetch bills data for the selected week using regular fetchData
      // We use fetchData for bills since we don't need to join it with another table here
      const billsData = await fetchData('bills', null, 'bill_date', {});
      
      if (billsData && Array.isArray(billsData)) {
        // Filter bills for the selected week
        const filteredBills = billsData.filter(bill => {
          const billDate = new Date(bill.bill_date);
          return billDate >= weekStart && billDate <= weekEnd && bill.status === 'active';
        });
        
        // Process daily sales data
        const dailySales = {};
        const paymentMethods = {};
        
        // Initialize daily data for all 7 days
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        for (let i = 0; i < 7; i++) {
          const currentDate = new Date(weekStart);
          currentDate.setDate(weekStart.getDate() + i);
          const dateStr = currentDate.toISOString().split('T')[0];
          const dayName = dayNames[currentDate.getDay()];
          
          dailySales[dateStr] = {
            day: dayName,
            date: dateStr,
            sales: 0,
            transactions: 0
          };
        }
        
        // Process bills data
        filteredBills.forEach(bill => {
          const billDate = bill.bill_date;
          const amount = parseFloat(bill.total_amount) || 0;
          
          // Daily sales
          if (dailySales[billDate]) {
            dailySales[billDate].sales += amount;
            dailySales[billDate].transactions += 1;
          }
          
          // Payment methods
          const paymentMethod = bill.payment_method || 'cash';
          paymentMethods[paymentMethod] = (paymentMethods[paymentMethod] || 0) + amount;
        });
        
        // Convert to arrays
        const dailySalesArray = Object.values(dailySales);
        const paymentMethodArray = Object.entries(paymentMethods).map(([method, value]) => ({
          name: method.charAt(0).toUpperCase() + method.slice(1),
          value: value,
          percentage: filteredBills.length > 0 ? ((value / filteredBills.reduce((sum, bill) => sum + parseFloat(bill.total_amount || 0), 0)) * 100).toFixed(1) : 0
        }));
        
        setWeeklySalesData(dailySalesArray);
        setPaymentMethodData(paymentMethodArray);
        
        // Calculate summary statistics
        const totalSales = filteredBills.reduce((sum, bill) => sum + (parseFloat(bill.total_amount) || 0), 0);
        const totalTransactions = filteredBills.length;
        const averagePerDay = dailySalesArray.length > 0 ? totalSales / 7 : 0;
        
        // Find best day
        let bestDayData = { day: '', sales: 0 };
        if (dailySalesArray.length > 0) {
          bestDayData = dailySalesArray.reduce((max, day) => 
            (day.sales || 0) > (max.sales || 0) ? day : max, dailySalesArray[0]);
        }
        
        setSummaryData({
          totalSales,
          totalTransactions,
          averagePerDay,
          bestDay: bestDayData.day || '',
          bestDaySales: bestDayData.sales || 0
        });
        
        // Fetch category-wise data from bill_items joined with categories
        await fetchCategoryWiseData(filteredBills);
        
      } else {
        // Handle case when no data is returned
        setWeeklySalesData([]);
        setPaymentMethodData([]);
        setCategoryWiseData([]);
        setSummaryData({
          totalSales: 0,
          totalTransactions: 0,
          averagePerDay: 0,
          bestDay: '',
          bestDaySales: 0
        });
      }
    } catch (error) {
      console.error('Error fetching weekly sales data:', error);
      setError('Failed to load weekly sales data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategoryWiseData = async (filteredBills) => {
    try {
      // Use fetchDataFromTwoTables to join bill_items with categories table
      // This will get bill_items with category names from the categories table
      const billItemsWithCategories = await fetchDataFromTwoTables(
        'bill_items',     // First table
        'categories',     // Second table  
        'category_id',    // Column from bill_items to join on
        'id',            // Column from categories to join on
        null,            // No setData function needed
        't1.id',         // Order by bill_items.id
        {}               // No where conditions - we'll filter after
      );
      
      if (billItemsWithCategories && Array.isArray(billItemsWithCategories)) {
        const billIds = filteredBills.map(bill => bill.id);
        const weekBillItems = billItemsWithCategories.filter(item => billIds.includes(item.bill_id));
        
        const categoryData = {};
        weekBillItems.forEach(item => {
          // Now we have the actual category name from the categories table (t2.name)
          // and the category_name field from bill_items table (t1.category_name)
          const categoryName = item.name || item.category_name || 'Other'; // t2.name takes priority
          const totalPrice = parseFloat(item.total_price) || 0;
          
          if (!categoryData[categoryName]) {
            categoryData[categoryName] = {
              name: categoryName,
              value: 0,
              quantity: 0
            };
          }
          
          categoryData[categoryName].value += totalPrice;
          categoryData[categoryName].quantity += parseInt(item.quantity) || 0;
        });
        
        const categoryArray = Object.values(categoryData);
        setCategoryWiseData(categoryArray);
      }
    } catch (error) {
      console.error('Error fetching category data:', error);
    }
  };

  useEffect(() => {
    fetchWeeklySalesData();
  }, [selectedWeek]);

  const handleWeekChange = (e) => {
    setSelectedWeek(e.target.value);
  };

  // Colors for pie charts
  const COLORS = ['#d90429', '#ef233c', '#f25c54', '#f72585', '#b5179e', '#7209b7', '#480ca8'];
  
  // Multi-color palette for bar charts (Daily Sales)
  const BAR_COLORS = ['#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#34495e'];
  
  // Gradient colors for area chart (Weekly Trend)
  const TREND_COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];

  if (isLoading) {
    return (
      <Layout>
        <Header title="Weekly Sales Report" />
        <div className="container-fluid">
          <div className="row justify-content-center mt-5">
            <div className="col-md-6 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-3">Loading weekly sales report...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="Weekly Sales Report" />
      <div className="weekly-sales-container">
        
        {/* Controls */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="weekSelect">Select Week Starting Date:</label>
              <input
                type="date"
                id="weekSelect"
                className="form-control"
                value={selectedWeek}
                onChange={handleWeekChange}
              />
            </div>
          </div>
          <div className="col-md-6 text-end">
            <button 
              className="btn btn-outline-primary"
              onClick={fetchWeeklySalesData}
              disabled={isLoading}
            >
              <i className="fas fa-sync-alt me-2"></i>
              Refresh Data
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger mb-4">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {/* Summary Cards */}
        <div className="row mb-4">
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="summary-card total-sales">
              <div className="card-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <div className="card-info">
                <h3>฿{summaryData.totalSales.toLocaleString()}</h3>
                <p>Total Weekly Sales</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="summary-card total-transactions">
              <div className="card-icon">
                <i className="fas fa-receipt"></i>
              </div>
              <div className="card-info">
                <h3>{summaryData.totalTransactions.toLocaleString()}</h3>
                <p>Total Transactions</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="summary-card average-sales">
              <div className="card-icon">
                <i className="fas fa-calendar-day"></i>
              </div>
              <div className="card-info">
                <h3>฿{summaryData.averagePerDay.toLocaleString()}</h3>
                <p>Average Per Day</p>
              </div>
            </div>
          </div>
          
          <div className="col-lg-3 col-md-6 mb-3">
            <div className="summary-card best-day">
              <div className="card-icon">
                <i className="fas fa-trophy"></i>
              </div>
              <div className="card-info">
                <h3>{summaryData.bestDay}</h3>
                <p>Best Day (฿{summaryData.bestDaySales.toLocaleString()})</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="row">
          {/* Bar Chart */}
          <div className="col-lg-6 col-md-12 mb-4">
            <div className="chart-panel">
              <div className="panel-header">
                <h5>Daily Sales Comparison</h5>
              </div>
              <div className="panel-content">
                {weeklySalesData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={weeklySalesData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="day" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '8px'
                        }}
                        formatter={(value, name) => [
                          `฿${value.toLocaleString()}`,
                          name === 'sales' ? 'Sales' : 'Transactions'
                        ]}
                      />
                      <Bar dataKey="sales" name="sales">
                        {weeklySalesData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={BAR_COLORS[index % BAR_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="no-data">
                    <i className="fas fa-chart-bar"></i>
                    <p>No sales data available for the selected week</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment Methods Pie Chart */}
          <div className="col-lg-6 col-md-12 mb-4">
            <div className="chart-panel">
              <div className="panel-header">
                <h5>Payment Methods Distribution</h5>
              </div>
              <div className="panel-content">
                {paymentMethodData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Pie
                        data={paymentMethodData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percentage }) => `${name}: ${percentage}%`}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {paymentMethodData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => [`฿${value.toLocaleString()}`, 'Amount']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="no-data">
                    <i className="fas fa-chart-pie"></i>
                    <p>No payment method data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Second Row of Charts */}
        <div className="row">
          {/* Category-wise Sales Pie Chart */}
          <div className="col-lg-6 col-md-12 mb-4">
            <div className="chart-panel">
              <div className="panel-header">
                <h5>Category-wise Sales</h5>
              </div>
              <div className="panel-content">
                {categoryWiseData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Pie
                        data={categoryWiseData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ฿${value.toLocaleString()}`}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {categoryWiseData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => [`฿${value.toLocaleString()}`, 'Sales']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="no-data">
                    <i className="fas fa-chart-pie"></i>
                    <p>No category data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sales Trend Area Chart */}
          <div className="col-lg-6 col-md-12 mb-4">
            <div className="chart-panel">
              <div className="panel-header">
                <h5>Weekly Sales Trend</h5>
              </div>
              <div className="panel-content">
                {weeklySalesData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <AreaChart data={weeklySalesData}>
                      <defs>
                        <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#FF6B6B" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#4ECDC4" stopOpacity={0.3}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="day" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [`฿${value.toLocaleString()}`, 'Sales']}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="sales" 
                        stroke="#FF6B6B" 
                        strokeWidth={3}
                        fill="url(#salesGradient)" 
                        fillOpacity={0.6}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="no-data">
                    <i className="fas fa-chart-area"></i>
                    <p>No trend data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="row">
          <div className="col-12">
            <div className="table-panel">
              <div className="panel-header">
                <h5>Detailed Weekly Report</h5>
              </div>
              <div className="panel-content">
                <div style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
                  <table className="table table-striped" style={{ minWidth: '800px', marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th style={{ whiteSpace: 'nowrap' }}>Day</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Date</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Sales Amount</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Transactions</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Average per Transaction</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Growth %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {weeklySalesData.length > 0 ? (
                        weeklySalesData.map((day, index) => {
                          const prevDaySales = index > 0 ? weeklySalesData[index - 1].sales : day.sales;
                          const growth = prevDaySales > 0 ? ((day.sales - prevDaySales) / prevDaySales * 100) : 0;
                          const avgPerTransaction = day.transactions > 0 ? day.sales / day.transactions : 0;
                          
                          return (
                            <tr key={index}>
                              <td className="font-weight-bold">{day.day}</td>
                              <td>{day.date}</td>
                              <td className="text-success">฿{day.sales.toLocaleString()}</td>
                              <td>{day.transactions}</td>
                              <td>฿{avgPerTransaction.toFixed(2)}</td>
                              <td className={growth >= 0 ? 'text-success' : 'text-danger'}>
                                {growth >= 0 ? '+' : ''}{growth.toFixed(1)}%
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center text-muted">
                            No data available for the selected week
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Analysis Tables */}
        <div className="row mt-4">
          {/* Payment Methods Table */}
          <div className="col-lg-6 col-md-12 mb-4">
            <div className="table-panel">
              <div className="panel-header">
                <h5>Payment Methods Analysis</h5>
              </div>
              <div className="panel-content">
                <div style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
                  <table className="table table-striped" style={{ minWidth: '500px', marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th style={{ whiteSpace: 'nowrap' }}>Payment Method</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Amount</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentMethodData.length > 0 ? (
                        paymentMethodData.map((method, index) => (
                          <tr key={index}>
                            <td className="font-weight-bold">{method.name}</td>
                            <td className="text-success">฿{method.value.toLocaleString()}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="progress flex-grow-1 me-2" style={{ height: '20px' }}>
                                  <div 
                                    className="progress-bar" 
                                    style={{ 
                                      width: `${method.percentage}%`, 
                                      backgroundColor: COLORS[index % COLORS.length] 
                                    }}
                                  ></div>
                                </div>
                                <span>{method.percentage}%</span>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center text-muted">
                            No payment method data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Category-wise Sales Table */}
          <div className="col-lg-6 col-md-12 mb-4">
            <div className="table-panel">
              <div className="panel-header">
                <h5>Category-wise Sales Analysis</h5>
              </div>
              <div className="panel-content">
                <div style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
                  <table className="table table-striped" style={{ minWidth: '600px', marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th style={{ whiteSpace: 'nowrap' }}>Category</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Sales Amount</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Quantity Sold</th>
                        <th style={{ whiteSpace: 'nowrap' }}>Avg Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryWiseData.length > 0 ? (
                        categoryWiseData.map((category, index) => {
                          const avgPrice = category.quantity > 0 ? category.value / category.quantity : 0;
                          return (
                            <tr key={index}>
                              <td className="font-weight-bold">{category.name}</td>
                              <td className="text-success">฿{category.value.toLocaleString()}</td>
                              <td>{category.quantity}</td>
                              <td>฿{avgPrice.toFixed(2)}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center text-muted">
                            No category data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
}

export default WeeklySales;
